import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FolderKanban, CheckSquare, Clock, Bell, Plus, Loader2, AlertTriangle, AlertCircle, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TaskModal from './TaskModal';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [stats, setStats] = useState({
    projects: 0,
    tasks: 0,
    hours: 0,
    reminders: 0,
    openIncidents: 0,
    pendingSolutions: 0,
    unansweredActions: 0
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [activeReminders, setActiveReminders] = useState([]);

  // Get first name from full name
  const getFirstName = (fullName) => {
    if (!fullName) return 'User';
    const parts = fullName.split(' ');
    return parts[0];
  };

  // Get user display name from various possible fields
  const getUserDisplayName = () => {
    if (!user) return 'User';
    // Try various name fields
    return user.name || user.field_12 || (user.field_12_raw?.full) || 'User';
  };

  useEffect(() => {
    if (user && token) {
      fetchDashboardData();
    }
  }, [user, token]);

  const fetchDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const projectsRes = await axios.get(`${API}/projects/user/${user.id}`, { 
        headers,
        params: { rows_per_page: 100 }  // Get more to filter
      });
      
      const tasksRes = await axios.get(`${API}/tasks/user/${user.id}`, { 
        headers,
        params: { rows_per_page: 100 }  // Get more to filter
      });
      
      const hoursRes = await axios.get(`${API}/hours/user/${user.id}/summary`, { headers });
      
      const remindersRes = await axios.get(`${API}/reminders/user/${user.id}`, { 
        headers,
        params: { rows_per_page: 10 }
      });

      // Fetch QC incidents
      const incidentsRes = await axios.get(`${API}/qc-incidents`, { headers });

      // Filter for ongoing projects only
      const ongoingProjects = projectsRes.data.records?.filter(p => {
        let status = '';
        if (Array.isArray(p.field_85_raw) && p.field_85_raw.length > 0) {
          // field_85_raw[0] is an object with identifier property
          status = (p.field_85_raw[0]?.identifier || '').toLowerCase();
        } else if (typeof p.field_85_raw === 'string') {
          status = p.field_85_raw.toLowerCase();
        } else if (typeof p.field_85 === 'string') {
          status = p.field_85.toLowerCase();
        }
        return status.includes('ongoing') || status.includes('active');
      }) || [];

      // Calculate projects past deadline
      const now = new Date();
      const projectsPastDeadline = (projectsRes.data.records || []).filter(p => {
        const deadline = p.field_246_raw || p.field_246;
        if (!deadline) return false;
        try {
          const deadlineDate = new Date(deadline);
          return deadlineDate < now;
        } catch {
          return false;
        }
      }).length;

      // Calculate projects not updated in 2 weeks
      const twoWeeksAgo = new Date();
      twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
      const projectsNotUpdated = (projectsRes.data.records || []).filter(p => {
        const lastUpdate = p.field_247_raw || p.field_247;
        if (!lastUpdate) return true; // No update = not updated
        try {
          const updateDate = new Date(lastUpdate);
          return updateDate < twoWeeksAgo;
        } catch {
          return true;
        }
      }).length;

      const openTasksCount = tasksRes.data.records?.filter(t => !t.field_50_raw).length || 0;
      
      // Calculate QC incident stats
      const incidents = incidentsRes.data.incidents || [];
      const openIncidents = incidents.filter(inc => inc.status === 'Unsolved').length;
      
      let pendingSolutions = 0;
      let unansweredActions = 0;
      
      incidents.forEach(incident => {
        const responses = incident.failure_points_responses || [];
        const failurePoints = incident.failure_points ? incident.failure_points.split('\n').filter(p => p.trim()) : [];
        
        // Count pending solutions (status = 'pending')
        pendingSolutions += responses.filter(r => r.status === 'pending').length;
        
        // Count unsolved action items
        let solvedCount = 0;
        failurePoints.forEach((_, index) => {
          const pointResponses = responses.filter(r => r.failure_point_index === index);
          const hasApprovedResponse = pointResponses.some(r => r.status === 'approved');
          if (hasApprovedResponse) {
            solvedCount++;
          }
        });
        unansweredActions += (failurePoints.length - solvedCount);
      });
      
      setStats({
        projects: projectsRes.data.total_records || 0,
        tasks: openTasksCount,
        hours: hoursRes.data.week || 0,
        reminders: remindersRes.data.records?.filter(r => !r.field_106_raw).length || 0,
        openIncidents,
        pendingSolutions,
        unansweredActions,
        projectsPastDeadline,
        projectsNotUpdated
      });

      setRecentProjects(ongoingProjects.slice(0, 5));
      
      const incompleteTasks = tasksRes.data.records?.filter(t => !t.field_50_raw) || [];
      
      // Apply the same order as My Tasks
      const savedOrder = localStorage.getItem('myTasksOrder');
      let orderedTasks = incompleteTasks;
      
      if (savedOrder) {
        const orderObj = JSON.parse(savedOrder);
        const openOrder = orderObj.open || [];
        
        if (openOrder.length > 0) {
          const ordered = [];
          const unordered = [];
          
          incompleteTasks.forEach(task => {
            const orderIndex = openOrder.indexOf(task.id);
            if (orderIndex !== -1) {
              ordered[orderIndex] = task;
            } else {
              unordered.push(task);
            }
          });
          
          orderedTasks = [...ordered.filter(Boolean), ...unordered];
        }
      }
      
      setUpcomingTasks(orderedTasks.slice(0, 5));
      
      const activeRemindersData = remindersRes.data.records?.filter(r => !r.field_106_raw) || [];
      setActiveReminders(activeRemindersData.slice(0, 5));

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { 
      name: 'Register Hours', 
      icon: Clock, 
      action: () => navigate('/my-hours'), 
      color: 'bg-blue-500' 
    },
    { 
      name: 'Add Task', 
      icon: CheckSquare, 
      action: () => setShowTaskModal(true), 
      color: 'bg-green-500' 
    },
    { 
      name: 'Add Reminder', 
      icon: Bell, 
      action: () => navigate('/reminders'), 
      color: 'bg-purple-500' 
    },
    { 
      name: 'New Project', 
      icon: FolderKanban, 
      action: () => navigate('/my-projects'), 
      color: 'bg-orange-500' 
    },
  ];

  const handleTaskSuccess = () => {
    fetchDashboardData(); // Refresh dashboard
    setShowTaskModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Welcome back, {getFirstName(getUserDisplayName())}!
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          Here's what's happening with your projects today.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.name}
                onClick={action.action}
                className="flex flex-col items-center justify-center p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 hover:shadow-md transition group"
              >
                <div className={`${action.color} p-3 rounded-lg mb-2 group-hover:scale-110 transition`}>
                  <Icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <span className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
                  {action.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Stats Grid - Two Rows */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">My Projects</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.projects}</p>
            </div>
            <FolderKanban className="w-10 h-10 text-blue-500 opacity-20" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active Tasks</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.tasks}</p>
            </div>
            <CheckSquare className="w-10 h-10 text-green-500 opacity-20" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Hours This Week</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.hours}</p>
            </div>
            <Clock className="w-10 h-10 text-purple-500 opacity-20" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active Reminders</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{stats.reminders}</p>
            </div>
            <Bell className="w-10 h-10 text-orange-500 opacity-20" />
          </div>
        </div>
      </div>

      {/* QC Incidents Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div 
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition"
          onClick={() => navigate('/qc-incidents')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">Open Incidents</p>
              <p className="text-2xl font-bold text-red-700 dark:text-red-300 mt-1">{stats.openIncidents}</p>
            </div>
            <AlertTriangle className="w-10 h-10 text-red-600 dark:text-red-400 opacity-50" />
          </div>
        </div>

        <div 
          className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition"
          onClick={() => navigate('/qc-incidents')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 dark:text-green-400 font-medium">Solutions to Review</p>
              <p className="text-2xl font-bold text-green-700 dark:text-green-300 mt-1">{stats.pendingSolutions}</p>
            </div>
            <AlertCircle className="w-10 h-10 text-green-600 dark:text-green-400 opacity-50" />
          </div>
        </div>

        <div 
          className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition"
          onClick={() => navigate('/qc-incidents')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">Unanswered Actions</p>
              <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-300 mt-1">{stats.unansweredActions}</p>
            </div>
            <HelpCircle className="w-10 h-10 text-yellow-600 dark:text-yellow-400 opacity-50" />
          </div>
        </div>
      </div>

      {/* Project Health Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div 
          className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition"
          onClick={() => navigate('/my-projects')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 dark:text-orange-400 font-medium">Projects Past Deadline</p>
              <p className="text-2xl font-bold text-orange-700 dark:text-orange-300 mt-1">{stats.projectsPastDeadline || 0}</p>
            </div>
            <AlertTriangle className="w-10 h-10 text-orange-600 dark:text-orange-400 opacity-50" />
          </div>
        </div>

        <div 
          className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition"
          onClick={() => navigate('/my-projects')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">Not Updated (2 weeks)</p>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300 mt-1">{stats.projectsNotUpdated || 0}</p>
            </div>
            <Clock className="w-10 h-10 text-amber-600 dark:text-amber-400 opacity-50" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Ongoing Projects</h2>
          </div>
          <div className="p-6">
            {recentProjects.length > 0 ? (
              <div className="space-y-3">
                {recentProjects.map((project) => (
                  <div 
                    key={project.id} 
                    onClick={() => navigate('/my-projects')}
                    className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition cursor-pointer"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {project.field_5_raw || 'Untitled Project'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {project.field_85 && typeof project.field_85 === 'string' ? project.field_85.replace(/<[^>]*>/g, '') : 'No status'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">No ongoing projects</p>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Open Tasks</h2>
          </div>
          <div className="p-6">
            {upcomingTasks.length > 0 ? (
              <div className="space-y-3">
                {upcomingTasks.map((task) => (
                  <div
                    key={task.id}
                    onClick={() => navigate('/my-tasks')}
                    className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition cursor-pointer"
                  >
                    <CheckSquare className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {task.field_43_raw || 'Untitled Task'}
                      </p>
                      {task.field_46 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">Due: {task.field_46}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">No tasks yet</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Active Reminders</h2>
        </div>
        <div className="p-6">
          {activeReminders.length > 0 ? (
            <div className="space-y-3">
              {activeReminders.map((reminder) => (
                <div key={reminder.id} className="flex items-start space-x-3 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                  <Bell className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {reminder.field_102_raw || 'Untitled Reminder'}
                    </p>
                    {reminder.field_103 && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {reminder.field_103}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">No active reminders</p>
          )}
        </div>
      </div>

      {/* Task Modal */}
      <TaskModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        task={null}
        onSuccess={handleTaskSuccess}
      />
    </div>
  );
};

export default Dashboard;
